<template>
  <div id="goods-return-note-create" class="group-setup">
    <v-progress-linear
      indeterminate
      color="blue"
      v-if="loading"
    ></v-progress-linear>

    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="onDismissed" :text="errors"></Alert>
      </v-flex>
    </v-layout>

    <v-form v-if="!loading">
      <v-row>
        <v-col cols="12" md="12" sm="12">
          <v-card class="pa-5" flat>
            <HeaderDividerBlock
              headerText="Create Goods Return Note"
            ></HeaderDividerBlock>
            <v-row>
              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Purchase Order Number</p>
                </v-col>
                <v-autocomplete
                  outlined
                  dense
                  v-model="form.po_number"
                  required
                  clearable
                  placeholder="Select Purchase Order Number"
                  :items="purchaseOrders"
                  item-text="po_number"
                  item-value="po_number"
                >
                </v-autocomplete>
              </v-col>

              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Goods Return Date</p>
                </v-col>
                <DatePicker
                  v-model="form.goods_return_date"
                  placeholder="Goods Return Date"
                  :max="new Date().toISOString().substr(0, 10)"
                ></DatePicker>
              </v-col>

              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Supplier</p>
                </v-col>
                <v-autocomplete
                  outlined
                  dense
                  v-model="form.supplier_id"
                  required
                  clearable
                  placeholder="Select Supplier"
                  :items="suppliers"
                  item-text="name"
                  item-value="id"
                >
                </v-autocomplete>
              </v-col>

              <v-col cols="12" sm="12">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Reason for Return</p>
                </v-col>
                <v-text-field
                  outlined
                  dense
                  v-model="form.reason_for_return"
                  required
                  clearable
                  placeholder="Reason for Return"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-col cols="12" md="12" sm="12">
              <GoodsReturnItems
                :products="products"
                @total="handleTotal"
                @items="handleItems"
              ></GoodsReturnItems>
            </v-col>

            <v-col cols="12" md="6" sm="12">
              <v-col>
                <v-col class="ma-0 pa-0">
                  <p class="text-h5 font-weight-bold">Summary</p>
                </v-col>
                <v-col class="px-7 pa-10 total-section">
                  <v-row class="justify-space-between">
                    <p class="text-subtitle-1 font-weight-bold">Sub Total</p>
                    <p>{{ calculateSubtotal() }}</p>
                  </v-row>
                </v-col>
              </v-col>
            </v-col>

            <v-card-actions class="mt-10">
              <v-row>
                <v-col cols="12" md="10" xs="6"></v-col>
                <v-col cols="12" md="2" xs="6">
                  <v-btn
                    v-if="hasPermission('goods_return_note.create')"
                    large
                    color="#00B3FF"
                    dark
                    depressed
                    block
                    @click="submit"
                  >
                    Create
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-form>

    <Snackbars
      :text="successMsg"
      :snackbar="snackbar"
      :color="'success'"
      @classSnackbar="closeSnackbar"
    />
  </div>
</template>

<script>
import DatePicker from "@/components/shared/DatePicker";
import Alert from "@/components/shared/Alert";
import Snackbars from "@/components/shared/Snackbars";
import HeaderDividerBlock from "@/components/shared/HeaderDividerBlock";
import { GoodsReturnService } from "@/services/goodsReturn-service.js";
import GoodsReturnItems from "@/components/GoodsReturn/GoodsReturnItems";
import { checkError } from "@/config/errors.js";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");

export default {
  name: "CreateGoodsReturnNoteForm",
  components: {
    Alert,
    Snackbars,
    DatePicker,
    HeaderDividerBlock,
    GoodsReturnItems,
  },
  data: () => ({
    loading: false,
    errors: null,
    snackbar: false,
    successMsg: "",
    form: {
      po_number: "",
      goods_return_date: new Date().toISOString().substr(0, 10),
      reason_for_return: "",
      supplier_id: "",
      sub_total: 0.0,
      items: [],
    },
    invoices: [],
    products: [],
    suppliers: [],
    po_number: "",
    purchaseOrders: [],
    statusOptions: ["Pending", "Approved", "Rejected"],
    search: "",
    page: 1,
  }),
  created() {
    this.fetchPurchaseOrders();
  },
  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
    dateValidation() {
      return (value) => {
        if (new Date(value) > new Date()) {
          return "Date cannot be in the future";
        }
        return true;
      };
    },
  },
  watch: {
    "form.po_number": async function(newPoNumber) {
      // Fetch products if PO Number is selected
      if (newPoNumber) {
        try {
          this.loading = true;
          const products = await GoodsReturnService.getItemsByPoNumber(
            newPoNumber
          );
          const supplierResponse = await GoodsReturnService.getSuppliers(
            newPoNumber
          );
          this.products = products; // Store the products for the selected PO
          this.suppliers = supplierResponse.data;
        } catch (err) {
          console.error(err);
        } finally {
          this.loading = false;
        }
      }
    },
  },
  methods: {
    async fetchPurchaseOrders() {
      try {
        const purchaseOrders = await GoodsReturnService.getPurchaseOrders();
        this.purchaseOrders = purchaseOrders.data;
      } catch (error) {
        console.error(error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.loading = false;
      }
    },

    handleItems(items) {
      this.form.items = items;
    },
    handleTotal(total) {
      this.form.total_amount = total;
    },
    async submit() {
      try {
        this.loading = true;
        this.errors = null;
        const response = await GoodsReturnService.createGrn(this.form);
        this.successMsg = response.message;
        this.snackbar = true;
        this.resetForm();
        this.$router.push({ name: "GoodsReturn" });
      } catch (error) {
        console.error(error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.loading = false;
      }
    },
    calculateSubtotal() {
      let subtotal = 0;
      this.form.items.forEach((item) => {
        subtotal += item.quantity_returned * item.unit_price;
      });
      this.form.sub_total = subtotal;
      return parseFloat(subtotal);
    },
    resetForm() {
      this.form = {
        goods_return_date: new Date().toISOString().substr(0, 10),
        supplier_id: "",
        reason_for_return: "",
        sub_total: 0.0,
        items: [],
      };
    },
  },
};
</script>
